// <TimeView> — T-143 (shell) + T-144/T-145 (tab bodies) +
// T-230 (Week-variant dispatch).
//
// Top-level page mounted at /time. Owns the page chrome (title + tab
// switch surface) and renders <TimeViewToolbar> + the per-tab body.
//
// Tab routing:
//   - tab=calendar + viewMode=Month → <CalendarTab>   (T-144)
//   - tab=calendar + viewMode=Week  → <CalendarWeek>  (T-230)
//   - tab=manage                    → <ManageRequestsTab> (T-145)
//
// viewMode comes from `useViewMode` (T-229) which is per-user-keyed.
// AuthContext provides the userId; an `'anonymous'` fallback keeps
// the component working in test contexts that don't wrap an
// AuthProvider.

import { useContext } from 'react';
import { AuthContext } from '../../contexts/AuthContext';
import CalendarTab from './CalendarTab';
import CalendarWeek from './CalendarWeek';
import ManageRequestsTab from './ManageRequestsTab';
import TimeViewToolbar from './TimeViewToolbar';
import { useTimeViewUrlState } from './timeViewUrlState';
import { useViewMode } from './useViewMode';

interface Props {
  /**
   * Optional site list for the toolbar dropdown. T-143 (Step 5) ships
   * an empty default — T-144 / T-145 will wire this from the response
   * of `GET /api/timeoff/calendar` / `GET /api/timeoff/requests`.
   */
  sites?: Array<{ id: string; name: string }>;
  teams?: Array<{ id: string; name: string }>;
}

export default function TimeView({ sites, teams }: Props = {}) {
  const { state } = useTimeViewUrlState();
  // Read AuthContext directly so tests that don't wrap an AuthProvider
  // still render. `useAuth` would throw; `useContext` returns null.
  const auth = useContext(AuthContext);
  const userId = auth?.me?.user.id ?? 'anonymous';
  const { viewMode } = useViewMode(userId);

  return (
    <div
      className="flex flex-col h-[calc(100vh-80px)] bg-white overflow-hidden"
      data-testid="time-view"
    >
      <header className="px-8 py-4 border-b border-slate-100">
        <h1 className="text-3xl font-bold text-slate-800 tracking-tight">
          People&rsquo;s time off
        </h1>
      </header>

      <TimeViewToolbar sites={sites} teams={teams} userId={userId} />

      <div className="flex-1 overflow-auto" data-testid="time-view-body">
        {state.tab === 'calendar' ? (
          viewMode === 'Week' ? <CalendarWeek /> : <CalendarTab />
        ) : (
          <ManageRequestsTab />
        )}
      </div>
    </div>
  );
}
